import React, { useLayoutEffect, useRef, useState } from "react";
import { Button, Box, Typography, TypographyProps } from "@mui/material";
import { useTranslation } from "react-i18next";

interface ReviewCommentBodyProps {
  comment: string;
  maxLines?: number;
  typographyProps?: TypographyProps;
  buttonSx?: object;
  onExpandedChange?: (expanded: boolean) => void;
}

const ReviewCommentBody: React.FC<ReviewCommentBodyProps> = ({
  comment,
  maxLines = 4,
  typographyProps,
  buttonSx,
  onExpandedChange,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isTruncatable, setIsTruncatable] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) {
      return;
    }
    setIsTruncatable(el.scrollHeight > el.clientHeight + 1);
  }, [comment, expanded, maxLines]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <Typography
        ref={textRef}
        variant="body2"
        {...typographyProps}
        sx={{
          color: "text.secondary",
          fontStyle: "italic",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          ...typographyProps?.sx,
          ...(!expanded
            ? {
                display: "-webkit-box",
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : {
                display: "block",
                overflow: "visible",
              }),
        }}
      >
        &ldquo;{comment}&rdquo;
      </Typography>
      {(isTruncatable || expanded) && (
        <Button
          size="small"
          onClick={() => {
            setExpanded((prev) => {
              const next = !prev;
              onExpandedChange?.(next);
              return next;
            });
          }}
          sx={{
            alignSelf: "flex-start",
            mt: 0.5,
            p: 0,
            minWidth: 0,
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "none",
            color: "info.main",
            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
            ...buttonSx,
          }}
        >
          {expanded
            ? t("common.showLess", { defaultValue: "Show less" })
            : t("common.readMore", { defaultValue: "Read more" })}
        </Button>
      )}
    </Box>
  );
};

export default ReviewCommentBody;
